import * as React from 'react';
import * as _ from 'underscore';

import { Messages } from '../../../codegen/Localize';
import { IActionButton } from '../../../commonInfra/contentActions/ActionPanelInterfaces';
import { Set } from '../../../ts/models/Set';
import { Icons } from '../Icons';
import { IReactTranslateService } from '../IReactTranslateService';
import { DeprecatedReactMenu } from './Menu';

export interface IActionTarget<TSummary, TDetail> {
  items: Set<TSummary>;
  detailedItem?: TDetail;
  allowedActions?: any;
}

/**
 * The ActionsMenu is a menu component with a button whose icon resembles an ellipsis, which we use
 * in various places throughout the app to display lists of actions that can be performed on various
 * entities.
 *
 * This is deprecated because, even though it's written in React, it doesn't use Shared Widgets.
 */
export class DeprecatedReactActionsMenu<TSummary, TDetail> extends React.Component<DeprecatedReactActionsMenu.Props<TSummary, TDetail>, any> {

  public static element = React.createFactory(DeprecatedReactActionsMenu);

  public static displayName = 'ActionsMenu';

  public render() {
    return DeprecatedReactMenu.element({
      menuButtonFactory: DeprecatedReactActionsMenuButton(this.props.buttonStyle, this.props.buttonHoverStyle),
      menuPanelFactory: DeprecatedReactActionsMenuPanel(this.props),
      style: this.props.style,
      menuPanelStyle: this.props.menuPanelStyle,
      tbTestId: 'action-menu'
    });
  }
}

export module DeprecatedReactActionsMenu {

  export interface Props<TSummary, TDetail> {
    actions: IActionButton<TSummary, TDetail>[];
    getActionTarget: () => IActionTarget<TSummary, TDetail>;
    translate: IReactTranslateService;
    buttonStyle?: React.CSSProperties;
    buttonHoverStyle?: React.CSSProperties;
    menuPanelStyle?: React.CSSProperties;
    style?: React.CSSProperties;
    testId?: string;
    attachUpdateTriggerToScope?: () => void;
  }

}

export function DeprecatedReactActionsMenuButton(
  buttonStyle: React.CSSProperties,
  buttonHoverStyle: React.CSSProperties,
): React.StatelessComponent<DeprecatedReactMenu.ButtonProps> {
  return function ActionsMenuButton(props: DeprecatedReactMenu.ButtonProps) {
    const highlight = props.hovered || props.menuOpen;
    const style: React.CSSProperties = _.extend({ cursor: 'pointer' }, buttonStyle);
    return React.createElement(Icons.actionIcon, {
      hovered: highlight,
      style: style,
      hoverStyle: buttonHoverStyle,
      title: Messages.moreActions(),
    });
  };
}

export function DeprecatedReactActionsMenuPanel<S, D>(menuProps: DeprecatedReactActionsMenu.Props<S, D>): React.StatelessComponent<DeprecatedReactMenu.PanelProps> {
  return function ActionsMenuPanel(props: DeprecatedReactMenu.PanelProps) {

    const actionTarget = menuProps.getActionTarget();

    const menuItems =
      _.map(
        _.filter(menuProps.actions, (action) => action.isAvailable(actionTarget.items, actionTarget.detailedItem, actionTarget.allowedActions)),
        (action) => React.createElement(DeprecatedReactActionsMenuItem, {
          key: action.name,
          action: action,
          actionTarget: actionTarget,
          menu: props.menu
        },
        menuProps.translate(action.translate || action.name)));

    return React.createElement('div', {
      ref: menuProps.attachUpdateTriggerToScope,
      className: 'tb-popup-menu-content tb-padded-small',
      'data-tb-test-id': 'action-menu-items'
    } as React.HTMLAttributes<HTMLDivElement>,
    menuItems);
  };
}

export interface DeprecatedReactActionsMenuItemProps<TSummary, TDetail> extends React.Props<any> {
  action: IActionButton<TSummary, TDetail>;
  actionTarget: IActionTarget<TSummary, TDetail>;
  menu: DeprecatedReactMenu;
}

export function DeprecatedReactActionsMenuItem<S, D>(props: DeprecatedReactActionsMenuItemProps<S, D>): JSX.Element {
  const { actionTarget, action, menu } = props;
  const disabled = action.isDisabled && action.isDisabled(actionTarget.items, actionTarget.detailedItem, actionTarget.allowedActions);
  return React.createElement('div', {
    className: 'tb-popup-menu-item' + (disabled ? ' tb-disabled' : ''),
    'data-tb-test-id': 'action-' + action.name,
    onClick: () => {
      menu.closeMenu();
      if (!disabled) {
        // TODO: (refactor) action.execute should take an IActionTarget instead of the individual parts.
        action.execute(actionTarget.items, actionTarget.detailedItem);
      }
    }
  } as React.HTMLAttributes<HTMLDivElement>,
  props.children);
}
